import { REPORT_IDS, REPORT_METADATA, type ReportId, toJalaliString } from "@kitchen/schemas";
import { Button, Card, Chip, faDigits, Panel, Seg } from "@kitchen/ui";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useMemo, useState } from "react";
import {
  api,
  getDailyExceptionsReport,
  getDeliveryDiscrepanciesReport,
  getLateChangesReport,
  getReportVisibilities,
  getReturnableContainersReport,
  getWasteAndReturnsReport,
  type RoleRow,
  setReportVisibility,
} from "../../api.js";
import { tehranToday } from "../../labels.js";
import { ErrorLine } from "../common.js";

function downloadCsv(filename: string, headers: string[], rows: (string | number | null | undefined)[][]) {
  const escapeCell = (cell: string | number | null | undefined) => {
    const s = String(cell ?? "").replace(/"/g, '""');
    return `"${s}"`;
  };
  const content =
    "\uFEFF" +
    [headers.map(escapeCell).join(","), ...rows.map((r) => r.map(escapeCell).join(","))].join("\r\n");
  const blob = new Blob([content], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

export function ReportsPage() {
  const queryClient = useQueryClient();
  const today = useMemo(() => tehranToday(), []);

  const [selectedReport, setSelectedReport] = useState<ReportId>("daily-exceptions");
  const [fromDate, setFromDate] = useState<string>(today);
  const [toDate, setToDate] = useState<string>(today);
  const [showSettingsModal, setShowSettingsModal] = useState(false);
  const [settingsError, setSettingsError] = useState<string | null>(null);

  // Queries for the 5 reports
  const dailyExceptionsQuery = useQuery({
    queryKey: ["report-daily-exceptions", fromDate, toDate],
    queryFn: () => getDailyExceptionsReport(fromDate, toDate),
    enabled: selectedReport === "daily-exceptions",
  });

  const wasteAndReturnsQuery = useQuery({
    queryKey: ["report-waste-and-returns", fromDate, toDate],
    queryFn: () => getWasteAndReturnsReport(fromDate, toDate),
    enabled: selectedReport === "waste-and-returns",
  });

  const returnableContainersQuery = useQuery({
    queryKey: ["report-returnable-containers", toDate],
    queryFn: () => getReturnableContainersReport(toDate),
    enabled: selectedReport === "returnable-containers",
  });

  const deliveryDiscrepanciesQuery = useQuery({
    queryKey: ["report-delivery-discrepancies", fromDate, toDate],
    queryFn: () => getDeliveryDiscrepanciesReport(fromDate, toDate),
    enabled: selectedReport === "delivery-discrepancies",
  });

  const lateChangesQuery = useQuery({
    queryKey: ["report-late-changes", fromDate, toDate],
    queryFn: () => getLateChangesReport(fromDate, toDate),
    enabled: selectedReport === "late-changes",
  });

  // Role permissions query
  const rolesQuery = useQuery({
    queryKey: ["roles"],
    queryFn: () => api<RoleRow[]>("/roles"),
    enabled: showSettingsModal,
  });

  const visibilitiesQuery = useQuery({
    queryKey: ["report-visibilities"],
    queryFn: getReportVisibilities,
    enabled: showSettingsModal,
  });

  const roles = rolesQuery.data ?? [];
  const visibilities = visibilitiesQuery.data ?? [];

  const handleToggleVisibility = async (roleId: string, reportId: ReportId, currentAllowed: boolean) => {
    setSettingsError(null);
    try {
      await setReportVisibility({
        roleId,
        reportId,
        allowed: !currentAllowed,
      });
      await queryClient.invalidateQueries({ queryKey: ["report-visibilities"] });
    } catch (err: unknown) {
      setSettingsError(err instanceof Error ? err.message : "خطا در به‌روزرسانی دسترسی");
    }
  };

  const handleExportCsv = () => {
    const jalaliRange = `${toJalaliString(fromDate)}_تا_${toJalaliString(toDate)}`;
    if (selectedReport === "daily-exceptions" && dailyExceptionsQuery.data) {
      const d = dailyExceptionsQuery.data;
      const headers = ["بخش", "سازمان", "واحد", "تاریخ", "عنوان/توضیح", "وضعیت/تعداد"];
      const rows: (string | number)[][] = [
        ...d.changes.map((c) => [
          "درخواست تغییر",
          c.orgName,
          c.unitName,
          toJalaliString(c.businessDate),
          c.note,
          c.status,
        ]),
        ...d.unregisteredUnits.map((u) => [
          "ثبت‌نشده",
          u.orgName,
          u.unitName,
          toJalaliString(u.date),
          "—",
          "بدون سفارش",
        ]),
        ...d.offMenuOrders.map((o) => [
          "خارج از منو",
          o.orgName,
          o.unitName,
          toJalaliString(o.businessDate),
          o.menuItemName,
          o.quantity,
        ]),
      ];
      downloadCsv(`گزارش_استثناهای_روز_${jalaliRange}.csv`, headers, rows);
    } else if (selectedReport === "waste-and-returns" && wasteAndReturnsQuery.data) {
      const d = wasteAndReturnsQuery.data;
      const headers = ["نوع", "انبار/سازمان", "کالا/سفارش", "مقدار/پرس", "واحد", "علت / دلیل"];
      const rows: (string | number)[][] = [
        ...d.inventoryWaste.map((w) => [
          "ضایعات انبار",
          w.warehouseName,
          w.itemName,
          w.quantityMilli / 1000,
          w.baseUnit,
          w.reason,
        ]),
        ...d.supplierReturns.map((s) => [
          "مرجوعی تأمین‌کننده",
          s.warehouseName,
          s.itemName,
          s.quantityMilli / 1000,
          s.baseUnit,
          s.reason,
        ]),
        ...d.orderReturns.map((o) => [
          "مرجوعی سفارش",
          o.orgName,
          o.unitName,
          toJalaliString(o.businessDate),
          "—",
          o.returnNote,
        ]),
      ];
      downloadCsv(`گزارش_ضایعات_و_مرجوعی_${jalaliRange}.csv`, headers, rows);
    } else if (selectedReport === "returnable-containers" && returnableContainersQuery.data) {
      const d = returnableContainersQuery.data;
      const headers = ["سازمان", "واحد", "مجموع بدهی ظروف", "خلاصه بدهی به تفکیک ظرف"];
      const rows = d.debtorUnits.map((u) => [u.orgName, u.unitName, u.totalNetContainers, u.debtSummary]);
      downloadCsv(`گزارش_ظروف_امانی_${toJalaliString(toDate)}.csv`, headers, rows);
    } else if (selectedReport === "delivery-discrepancies" && deliveryDiscrepanciesQuery.data) {
      const d = deliveryDiscrepanciesQuery.data;
      const headers = ["نوع", "سازمان", "واحد", "تاریخ", "سفارش", "تحویل", "اختلاف", "شرح/یادداشت"];
      const rows: (string | number)[][] = [
        ...d.discrepancies.map((x) => [
          "مغایرت تعداد",
          x.orgName,
          x.unitName,
          toJalaliString(x.businessDate),
          x.orderedQuantity,
          x.deliveredQuantity,
          x.diffQuantity,
          x.note ?? "—",
        ]),
        ...d.complaints.map((c) => [
          "شکایت/مشکل رابط",
          c.orgName,
          c.unitName,
          toJalaliString(c.businessDate),
          "—",
          "—",
          c.kind,
          c.note,
        ]),
      ];
      downloadCsv(`گزارش_اختلاف_تحویل_و_شکایت_${jalaliRange}.csv`, headers, rows);
    } else if (selectedReport === "late-changes" && lateChangesQuery.data) {
      const d = lateChangesQuery.data;
      const headers = ["نوع", "سازمان", "واحد", "تاریخ", "مقدار/اختلاف", "وضعیت", "توضیح"];
      const rows: (string | number)[][] = [
        ...d.lateOrders.map((o) => [
          "سفارش دیرهنگام",
          o.orgName,
          o.unitName,
          toJalaliString(o.businessDate),
          o.totalQuantity,
          o.status,
          o.enteredByStaff ? "ثبت دستی توسط مدیر" : "ثبت توسط رابط",
        ]),
        ...d.changeRequests.map((c) => [
          "درخواست تغییر",
          c.orgName,
          c.unitName,
          toJalaliString(c.businessDate),
          c.portionsDifference,
          c.status,
          c.note,
        ]),
        ...d.returnedOrders.map((r) => [
          "سفارش برگشتی",
          r.orgName,
          r.unitName,
          toJalaliString(r.businessDate),
          "—",
          r.status,
          r.returnNote,
        ]),
      ];
      downloadCsv(`گزارش_تغییرات_دیرهنگام_${jalaliRange}.csv`, headers, rows);
    }
  };

  return (
    <div className="stack" style={{ gap: "var(--s4)" }}>
      <style>{`
        @media print {
          .no-print, header.top, nav.pills, aside.rail, .top-bar {
            display: none !important;
          }
          body, #root, .app-shell, .main {
            background: #fff !important;
            padding: 0 !important;
            margin: 0 !important;
          }
          .a4-sheet {
            box-shadow: none !important;
            border: none !important;
            width: 100% !important;
            max-width: 100% !important;
            padding: 5mm !important;
          }
        }
        .a4-sheet {
          background: #fff;
          color: #111;
          border-radius: var(--r-card);
          padding: var(--s4);
          border: 1px solid var(--border);
        }
      `}</style>

      {/* Top Filter and Actions Bar */}
      <div className="card no-print stack" style={{ gap: "var(--s3)", padding: "var(--s3)" }}>
        <div className="row wrap" style={{ justifyContent: "space-between", alignItems: "center" }}>
          <Seg<ReportId>
            label="انتخاب گزارش"
            value={selectedReport}
            onChange={setSelectedReport}
            options={REPORT_IDS.map((id) => ({
              value: id,
              label: REPORT_METADATA[id].title,
            }))}
          />

          <Button
            variant="ghost"
            icon="settings"
            onClick={() => {
              setShowSettingsModal(true);
            }}
          >
            تنظیم دسترسی نقش‌ها
          </Button>
        </div>

        <div className="row wrap" style={{ justifyContent: "space-between", alignItems: "center" }}>
          <div className="row wrap" style={{ alignItems: "center", gap: "var(--s3)" }}>
            <div className="row" style={{ alignItems: "center", gap: "var(--s2)" }}>
              <span className="b">از تاریخ:</span>
              <input
                className="input"
                type="date"
                value={fromDate}
                onChange={(e) => {
                  setFromDate(e.target.value);
                }}
                style={{ width: "150px" }}
              />
              <span className="hint ink2">({toJalaliString(fromDate)})</span>
            </div>

            <div className="row" style={{ alignItems: "center", gap: "var(--s2)" }}>
              <span className="b">تا تاریخ:</span>
              <input
                className="input"
                type="date"
                value={toDate}
                onChange={(e) => {
                  setToDate(e.target.value);
                }}
                style={{ width: "150px" }}
              />
              <span className="hint ink2">({toJalaliString(toDate)})</span>
            </div>
          </div>

          <div className="row" style={{ gap: "var(--s2)" }}>
            <Button id="btn-export-csv" variant="default" onClick={handleExportCsv}>
              خروجی اکسل (CSV)
            </Button>
            <Button
              id="btn-print-pdf"
              variant="primary"
              onClick={() => {
                window.print();
              }}
            >
              چاپ برگه A4 / PDF
            </Button>
          </div>
        </div>
      </div>

      {/* Main Report View (A4 Sheet ready) */}
      <div className="a4-sheet stack" style={{ gap: "var(--s4)" }}>
        <div
          className="row wrap"
          style={{
            justifyContent: "space-between",
            alignItems: "center",
            borderBottom: "2px solid var(--border)",
            paddingBottom: "var(--s2)",
          }}
        >
          <div>
            <h1 className="t-title" style={{ margin: 0 }}>
              {REPORT_METADATA[selectedReport].title}
            </h1>
            <span className="hint ink2">{REPORT_METADATA[selectedReport].description}</span>
          </div>
          <div className="stack" style={{ textAlign: "left", gap: "2px" }}>
            <span className="b">
              بازه زمانی: {toJalaliString(fromDate)} الی {toJalaliString(toDate)}
            </span>
            <span className="hint ink2">تاریخ چاپ: {toJalaliString(today)}</span>
          </div>
        </div>

        {/* 1. Daily Exceptions Report */}
        {selectedReport === "daily-exceptions" && (
          <div className="stack" style={{ gap: "var(--s4)" }}>
            {dailyExceptionsQuery.isLoading ? (
              <p className="ink2">در حال بارگذاری گزارش...</p>
            ) : dailyExceptionsQuery.data ? (
              <>
                <div className="grid" style={{ gridTemplateColumns: "1fr 1fr 1fr", gap: "var(--s3)" }}>
                  <Card>
                    <span className="hint ink2">درخواست‌های تغییر</span>
                    <strong className="t-title">
                      {faDigits(dailyExceptionsQuery.data.summary.totalChanges)}
                    </strong>
                  </Card>
                  <Card>
                    <span className="hint ink2">واحدهای ثبت‌نشده</span>
                    <strong className="t-title">
                      {faDigits(dailyExceptionsQuery.data.summary.totalUnregistered)}
                    </strong>
                  </Card>
                  <Card>
                    <span className="hint ink2">سفارش‌های خارج از منو</span>
                    <strong className="t-title">
                      {faDigits(dailyExceptionsQuery.data.summary.totalOffMenu)}
                    </strong>
                  </Card>
                </div>

                <Panel title="درخواست‌های تغییر سفارش">
                  {dailyExceptionsQuery.data.changes.length === 0 ? (
                    <p className="ink2">هیچ درخواست تغییری در این بازه ثبت نشده است.</p>
                  ) : (
                    <div className="table-wrap">
                      <table>
                        <thead>
                          <tr>
                            <th>سازمان</th>
                            <th>واحد</th>
                            <th>تاریخ وعده</th>
                            <th>توضیحات درخواست</th>
                            <th>وضعیت</th>
                            <th>تصمیم مدیر</th>
                          </tr>
                        </thead>
                        <tbody>
                          {dailyExceptionsQuery.data.changes.map((c) => (
                            <tr key={c.orderId}>
                              <td>{c.orgName}</td>
                              <td className="b">{c.unitName}</td>
                              <td>{toJalaliString(c.businessDate)}</td>
                              <td>{c.note}</td>
                              <td>
                                <Chip tone={c.status === "accepted" ? "ok" : "warn"}>{c.status}</Chip>
                              </td>
                              <td>{c.decisionNote || "—"}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </Panel>

                <Panel title="واحدهای بدون ثبت سفارش">
                  {dailyExceptionsQuery.data.unregisteredUnits.length === 0 ? (
                    <p className="ink2">همه‌ی واحدها در روزهای کاری سفارش خود را ثبت کرده‌اند.</p>
                  ) : (
                    <div className="table-wrap">
                      <table>
                        <thead>
                          <tr>
                            <th>سازمان</th>
                            <th>واحد</th>
                            <th>تاریخ</th>
                            <th>وضعیت</th>
                          </tr>
                        </thead>
                        <tbody>
                          {dailyExceptionsQuery.data.unregisteredUnits.map((u, i) => (
                            <tr key={i}>
                              <td>{u.orgName}</td>
                              <td className="b">{u.unitName}</td>
                              <td>{toJalaliString(u.date)}</td>
                              <td>
                                <Chip tone="coral">ثبت‌نشده</Chip>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </Panel>

                <Panel title="اقلام خارج از منو و ویژه">
                  {dailyExceptionsQuery.data.offMenuOrders.length === 0 ? (
                    <p className="ink2">سفارش خارج از منو در این بازه وجود ندارد.</p>
                  ) : (
                    <div className="table-wrap">
                      <table>
                        <thead>
                          <tr>
                            <th>سازمان</th>
                            <th>واحد</th>
                            <th>تاریخ</th>
                            <th>نام غذای درخواستی</th>
                            <th>تعداد پرس</th>
                            <th>یادداشت</th>
                          </tr>
                        </thead>
                        <tbody>
                          {dailyExceptionsQuery.data.offMenuOrders.map((o, i) => (
                            <tr key={i}>
                              <td>{o.orgName}</td>
                              <td className="b">{o.unitName}</td>
                              <td>{toJalaliString(o.businessDate)}</td>
                              <td>{o.menuItemName}</td>
                              <td>{faDigits(o.quantity)}</td>
                              <td>{o.note || "—"}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </Panel>
              </>
            ) : null}
          </div>
        )}

        {/* 2. Waste and Returns Report */}
        {selectedReport === "waste-and-returns" && (
          <div className="stack" style={{ gap: "var(--s4)" }}>
            {wasteAndReturnsQuery.isLoading ? (
              <p className="ink2">در حال بارگذاری گزارش...</p>
            ) : wasteAndReturnsQuery.data ? (
              <>
                <div className="grid" style={{ gridTemplateColumns: "1fr 1fr 1fr", gap: "var(--s3)" }}>
                  <Card>
                    <span className="hint ink2">حواله‌های ضایعات</span>
                    <strong className="t-title">
                      {faDigits(wasteAndReturnsQuery.data.summary.totalWasteVouchers)}
                    </strong>
                  </Card>
                  <Card>
                    <span className="hint ink2">مرجوعی به تأمین‌کننده</span>
                    <strong className="t-title">
                      {faDigits(wasteAndReturnsQuery.data.summary.totalSupplierReturns)}
                    </strong>
                  </Card>
                  <Card>
                    <span className="hint ink2">سفارشات برگشتی</span>
                    <strong className="t-title">
                      {faDigits(wasteAndReturnsQuery.data.summary.totalOrderReturns)}
                    </strong>
                  </Card>
                </div>

                <Panel title="ضایعات مواد اولیه و کالا">
                  {wasteAndReturnsQuery.data.inventoryWaste.length === 0 ? (
                    <p className="ink2">ضایعاتی در این بازه ثبت نشده است.</p>
                  ) : (
                    <div className="table-wrap">
                      <table>
                        <thead>
                          <tr>
                            <th>شماره حواله</th>
                            <th>تاریخ</th>
                            <th>انبار</th>
                            <th>نام کالا</th>
                            <th>مقدار</th>
                            <th>علت و دلیل ضایعات</th>
                          </tr>
                        </thead>
                        <tbody>
                          {wasteAndReturnsQuery.data.inventoryWaste.map((w, i) => (
                            <tr key={i}>
                              <td>{w.voucherNumber ?? "—"}</td>
                              <td>{toJalaliString(w.date)}</td>
                              <td>{w.warehouseName}</td>
                              <td className="b">{w.itemName}</td>
                              <td>
                                {faDigits(w.quantityMilli / 1000)} {w.baseUnit}
                              </td>
                              <td>{w.reason}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </Panel>

                <Panel title="مرجوعی کالا به تأمین‌کننده">
                  {wasteAndReturnsQuery.data.supplierReturns.length === 0 ? (
                    <p className="ink2">مرجوعی به تأمین‌کننده در این بازه ثبت نشده است.</p>
                  ) : (
                    <div className="table-wrap">
                      <table>
                        <thead>
                          <tr>
                            <th>شماره حواله</th>
                            <th>تاریخ</th>
                            <th>انبار</th>
                            <th>نام کالا</th>
                            <th>مقدار</th>
                            <th>علت مرجوعی</th>
                          </tr>
                        </thead>
                        <tbody>
                          {wasteAndReturnsQuery.data.supplierReturns.map((s, i) => (
                            <tr key={i}>
                              <td>{s.voucherNumber ?? "—"}</td>
                              <td>{toJalaliString(s.date)}</td>
                              <td>{s.warehouseName}</td>
                              <td className="b">{s.itemName}</td>
                              <td>
                                {faDigits(s.quantityMilli / 1000)} {s.baseUnit}
                              </td>
                              <td>{s.reason}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </Panel>

                <Panel title="سفارشات مرجوعی سازمان‌ها">
                  {wasteAndReturnsQuery.data.orderReturns.length === 0 ? (
                    <p className="ink2">سفارش مرجوعی در این بازه ثبت نشده است.</p>
                  ) : (
                    <div className="table-wrap">
                      <table>
                        <thead>
                          <tr>
                            <th>سازمان</th>
                            <th>واحد</th>
                            <th>تاریخ وعده</th>
                            <th>علت و یادداشت برگشت</th>
                            <th>وضعیت</th>
                          </tr>
                        </thead>
                        <tbody>
                          {wasteAndReturnsQuery.data.orderReturns.map((o) => (
                            <tr key={o.orderId}>
                              <td>{o.orgName}</td>
                              <td className="b">{o.unitName}</td>
                              <td>{toJalaliString(o.businessDate)}</td>
                              <td>{o.returnNote}</td>
                              <td>
                                <Chip tone="coral">{o.status}</Chip>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </Panel>
              </>
            ) : null}
          </div>
        )}

        {/* 3. Returnable Containers Report */}
        {selectedReport === "returnable-containers" && (
          <div className="stack" style={{ gap: "var(--s4)" }}>
            {returnableContainersQuery.isLoading ? (
              <p className="ink2">در حال بارگذاری گزارش...</p>
            ) : returnableContainersQuery.data ? (
              <>
                <div className="grid" style={{ gridTemplateColumns: "1fr 1fr 1fr", gap: "var(--s3)" }}>
                  <Card>
                    <span className="hint ink2">واحدهای طرف امانت</span>
                    <strong className="t-title">
                      {faDigits(returnableContainersQuery.data.summary.totalTrackedUnits)}
                    </strong>
                  </Card>
                  <Card>
                    <span className="hint ink2">واحدهای بدهکار ظروف</span>
                    <strong className="t-title">
                      {faDigits(returnableContainersQuery.data.summary.totalDebtorUnits)}
                    </strong>
                  </Card>
                  <Card>
                    <span className="hint ink2">مجموع ظروف دست مشتریان</span>
                    <strong className="t-title">
                      {faDigits(returnableContainersQuery.data.summary.totalOutstandingContainers)}
                    </strong>
                  </Card>
                </div>

                <Panel title="فهرست واحدهای بدهکار ظروف امانی">
                  {returnableContainersQuery.data.debtorUnits.length === 0 ? (
                    <p className="ink2">هیچ واحد بدهکاری وجود ندارد؛ همه ظروف عودت داده شده‌اند.</p>
                  ) : (
                    <div className="table-wrap">
                      <table>
                        <thead>
                          <tr>
                            <th>سازمان</th>
                            <th>واحد</th>
                            <th>مجموع ظروف امانی</th>
                            <th>تفکیک بدهی ظروف</th>
                          </tr>
                        </thead>
                        <tbody>
                          {returnableContainersQuery.data.debtorUnits.map((u) => (
                            <tr key={u.unitId}>
                              <td>{u.orgName}</td>
                              <td className="b">{u.unitName}</td>
                              <td>
                                <Chip tone="warn">{faDigits(u.totalNetContainers)} عدد</Chip>
                              </td>
                              <td>{u.debtSummary}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </Panel>
              </>
            ) : null}
          </div>
        )}

        {/* 4. Delivery Discrepancies and Complaints Report */}
        {selectedReport === "delivery-discrepancies" && (
          <div className="stack" style={{ gap: "var(--s4)" }}>
            {deliveryDiscrepanciesQuery.isLoading ? (
              <p className="ink2">در حال بارگذاری گزارش...</p>
            ) : deliveryDiscrepanciesQuery.data ? (
              <>
                <div className="grid" style={{ gridTemplateColumns: "1fr 1fr 1fr 1fr", gap: "var(--s3)" }}>
                  <Card>
                    <span className="hint ink2">ایستگاه‌های دارای مغایرت</span>
                    <strong className="t-title">
                      {faDigits(deliveryDiscrepanciesQuery.data.summary.totalDiscrepancyStops)}
                    </strong>
                  </Card>
                  <Card>
                    <span className="hint ink2">مجموع پرس کسری تحویل</span>
                    <strong className="t-title">
                      {faDigits(deliveryDiscrepanciesQuery.data.summary.totalShortPortions)}
                    </strong>
                  </Card>
                  <Card>
                    <span className="hint ink2">شکایت‌های ثبت‌شده</span>
                    <strong className="t-title">
                      {faDigits(deliveryDiscrepanciesQuery.data.summary.totalComplaints)}
                    </strong>
                  </Card>
                  <Card>
                    <span className="hint ink2">شکایت‌های در انتظار بررسی</span>
                    <strong className="t-title">
                      {faDigits(deliveryDiscrepanciesQuery.data.summary.unresolvedComplaints)}
                    </strong>
                  </Card>
                </div>

                <Panel title="اختلاف و کسری‌های تحویل">
                  {deliveryDiscrepanciesQuery.data.discrepancies.length === 0 ? (
                    <p className="ink2">مغایرت تحویلی در این بازه ثبت نشده است.</p>
                  ) : (
                    <div className="table-wrap">
                      <table>
                        <thead>
                          <tr>
                            <th>سازمان</th>
                            <th>واحد</th>
                            <th>تاریخ</th>
                            <th>تعداد سفارش</th>
                            <th>تعداد تحویل</th>
                            <th>میزان کسری</th>
                            <th>یادداشت راننده</th>
                          </tr>
                        </thead>
                        <tbody>
                          {deliveryDiscrepanciesQuery.data.discrepancies.map((d, i) => (
                            <tr key={i}>
                              <td>{d.orgName}</td>
                              <td className="b">{d.unitName}</td>
                              <td>{toJalaliString(d.businessDate)}</td>
                              <td>{faDigits(d.orderedQuantity)}</td>
                              <td>{faDigits(d.deliveredQuantity)}</td>
                              <td>
                                <Chip tone="coral">{faDigits(d.diffQuantity)} پرس</Chip>
                              </td>
                              <td>{d.note || "—"}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </Panel>

                <Panel title="مشکلات و شکایت‌های گزارش‌شده رابطین">
                  {deliveryDiscrepanciesQuery.data.complaints.length === 0 ? (
                    <p className="ink2">شکایتی در این بازه ثبت نشده است.</p>
                  ) : (
                    <div className="table-wrap">
                      <table>
                        <thead>
                          <tr>
                            <th>سازمان</th>
                            <th>واحد</th>
                            <th>تاریخ</th>
                            <th>نوع مشکل</th>
                            <th>شرح شکایت</th>
                            <th>وضعیت رسیدگی</th>
                          </tr>
                        </thead>
                        <tbody>
                          {deliveryDiscrepanciesQuery.data.complaints.map((c) => (
                            <tr key={c.problemId}>
                              <td>{c.orgName}</td>
                              <td className="b">{c.unitName}</td>
                              <td>{toJalaliString(c.businessDate)}</td>
                              <td>
                                <Chip tone="warn">{c.kind}</Chip>
                              </td>
                              <td>{c.note}</td>
                              <td>{c.resolvedAt ? "رسیدگی شد" : "در انتظار رسیدگی"}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </Panel>
              </>
            ) : null}
          </div>
        )}

        {/* 5. Late Changes and Returns Report */}
        {selectedReport === "late-changes" && (
          <div className="stack" style={{ gap: "var(--s4)" }}>
            {lateChangesQuery.isLoading ? (
              <p className="ink2">در حال بارگذاری گزارش...</p>
            ) : lateChangesQuery.data ? (
              <>
                <div className="grid" style={{ gridTemplateColumns: "1fr 1fr 1fr 1fr", gap: "var(--s3)" }}>
                  <Card>
                    <span className="hint ink2">سفارش‌های دیرهنگام</span>
                    <strong className="t-title">
                      {faDigits(lateChangesQuery.data.summary.totalLateOrders)}
                    </strong>
                  </Card>
                  <Card>
                    <span className="hint ink2">درخواست‌های تغییر</span>
                    <strong className="t-title">
                      {faDigits(lateChangesQuery.data.summary.totalChangeRequests)}
                    </strong>
                  </Card>
                  <Card>
                    <span className="hint ink2">درخواست‌های پذیرفته</span>
                    <strong className="t-title">
                      {faDigits(lateChangesQuery.data.summary.acceptedChangeRequests)}
                    </strong>
                  </Card>
                  <Card>
                    <span className="hint ink2">سفارش‌های برگشتی</span>
                    <strong className="t-title">
                      {faDigits(lateChangesQuery.data.summary.totalReturnedOrders)}
                    </strong>
                  </Card>
                </div>

                <Panel title="سفارش‌های ثبت‌شده بعد از مهلت (دیرهنگام)">
                  {lateChangesQuery.data.lateOrders.length === 0 ? (
                    <p className="ink2">سفارش دیرهنگامی در این بازه وجود ندارد.</p>
                  ) : (
                    <div className="table-wrap">
                      <table>
                        <thead>
                          <tr>
                            <th>سازمان</th>
                            <th>واحد</th>
                            <th>تاریخ وعده</th>
                            <th>پرس کل</th>
                            <th>روش ثبت</th>
                            <th>وضعیت</th>
                          </tr>
                        </thead>
                        <tbody>
                          {lateChangesQuery.data.lateOrders.map((o) => (
                            <tr key={o.orderId}>
                              <td>{o.orgName}</td>
                              <td className="b">{o.unitName}</td>
                              <td>{toJalaliString(o.businessDate)}</td>
                              <td>{faDigits(o.totalQuantity)}</td>
                              <td>{o.enteredByStaff ? "توسط مدیر" : "توسط رابط"}</td>
                              <td>
                                <Chip tone="warn">{o.status}</Chip>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </Panel>

                <Panel title="درخواست‌های تغییر بعد از قفل پخت و تصمیمات مدیر">
                  {lateChangesQuery.data.changeRequests.length === 0 ? (
                    <p className="ink2">درخواست تغییری ثبت نشده است.</p>
                  ) : (
                    <div className="table-wrap">
                      <table>
                        <thead>
                          <tr>
                            <th>سازمان</th>
                            <th>واحد</th>
                            <th>تاریخ</th>
                            <th>اختلاف پرس</th>
                            <th>توضیحات رابط</th>
                            <th>وضعیت</th>
                            <th>تصمیم مدیر</th>
                          </tr>
                        </thead>
                        <tbody>
                          {lateChangesQuery.data.changeRequests.map((c) => (
                            <tr key={c.requestId}>
                              <td>{c.orgName}</td>
                              <td className="b">{c.unitName}</td>
                              <td>{toJalaliString(c.businessDate)}</td>
                              <td>
                                <span className={c.portionsDifference >= 0 ? "ok" : "coral"}>
                                  {c.portionsDifference >= 0 ? "+" : ""}
                                  {faDigits(c.portionsDifference)}
                                </span>
                              </td>
                              <td>{c.note}</td>
                              <td>
                                <Chip tone={c.status === "accepted" ? "ok" : "warn"}>{c.status}</Chip>
                              </td>
                              <td>{c.decisionNote || "—"}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </Panel>
              </>
            ) : null}
          </div>
        )}
      </div>

      {/* Role Visibility Settings Modal */}
      {showSettingsModal && (
        <div className="modal-backdrop">
          <div
            className="card modal-card"
            style={{ maxWidth: "750px", width: "95%", maxHeight: "90vh", overflowY: "auto" }}
          >
            <div className="row wrap-between" style={{ alignItems: "center", marginBottom: "var(--s3)" }}>
              <h2 className="t-title">تنظیم دسترسی گزارش‌ها بر اساس نقش سازمانی</h2>
              <Button
                variant="ghost"
                onClick={() => {
                  setShowSettingsModal(false);
                }}
              >
                بستن
              </Button>
            </div>

            <p className="hint ink2">
              مدیر ارشد تعیین می‌کند کدام گزارش‌ها برای هر نقش در سامانه نمایش داده شوند.
            </p>
            <ErrorLine error={settingsError} />

            <div className="table-wrap">
              <table>
                <thead>
                  <tr>
                    <th>نقش سازمانی</th>
                    {REPORT_IDS.map((id) => (
                      <th key={id} style={{ fontSize: "0.8rem", textAlign: "center" }}>
                        {REPORT_METADATA[id].title}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {roles.map((r) => {
                    const isSenior = r.systemKey === "senior-manager";
                    return (
                      <tr key={r.id}>
                        <td className="b">{r.name}</td>
                        {REPORT_IDS.map((repId) => {
                          const vis = visibilities.find(
                            (v) => v.roleId === (r.systemKey ?? r.id) && v.reportId === repId,
                          );
                          const isAllowed = isSenior
                            ? true
                            : vis !== undefined
                              ? vis.allowed
                              : r.systemKey === "kitchen-manager" || r.systemKey === "operations-manager";

                          return (
                            <td key={repId} style={{ textAlign: "center" }}>
                              <input
                                type="checkbox"
                                checked={isAllowed}
                                disabled={isSenior}
                                onChange={() => {
                                  void handleToggleVisibility(r.systemKey ?? r.id, repId, isAllowed);
                                }}
                              />
                            </td>
                          );
                        })}
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
